import React from 'react';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import './vat-info-modal.scss';

interface VatInfoModalProps {
  show: boolean;
  onHide: () => void;
  selectedVat?: string; // The selected VAT rate (0, 6, 12, 25)
}

const VatInfoModal: React.FC<VatInfoModalProps> = ({ show, onHide, selectedVat }) => {
  const { t } = useTranslation();
  
  // Determine which VAT rate to show
  const vatRateToShow = selectedVat || '25';
  
  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      centered
      className="vat-info-modal"
    >
      <Modal.Header>
        <Modal.Title>{t('VAT_INFO_MODAL_TITLE')}</Modal.Title>
        <div 
          style={{color: 'white', background: 'transparent', border: 'none', cursor: 'pointer'}} 
          onClick={onHide}
          aria-label="Close"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path 
              d="M18 6L6 18M6 6L18 18" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </Modal.Header>
      <Modal.Body>
        <div className="modal-body-content">
          {/* 25% VAT */}
          {vatRateToShow === '25' && (
            <div className="vat-single-card vat-25">
              <div className="vat-card-header">
                <div className="vat-badge">25%</div>
                <h3>{t('VAT')}</h3>
              </div>
              <p className="section-description">{t('VAT_25_DESCRIPTION')}</p>
              <ul className="vat-list">
                <li>{t('VAT_25_STAFFING')}</li>
                <li>{t('VAT_25_CONSULTING')}</li>
                <li>{t('VAT_25_RENTAL')}</li>
                <li>{t('VAT_25_FEES')}</li>
                <li>{t('VAT_25_OTHER')}</li>
              </ul>
              <div className="info-badge">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M8 1.5C4.41 1.5 1.5 4.41 1.5 8C1.5 11.59 4.41 14.5 8 14.5C11.59 14.5 14.5 11.59 14.5 8C14.5 4.41 11.59 1.5 8 1.5ZM8.75 11.25H7.25V7.25H8.75V11.25ZM8.75 5.75H7.25V4.25H8.75V5.75Z" fill="currentColor"/>
                </svg>
                <span>{t('VAT_25_NOTE')}</span>
              </div>
            </div>
          )}

          {/* 6% VAT */}
          {vatRateToShow === '6' && (
            <div className="vat-single-card vat-6">
              <div className="vat-card-header">
                <div className="vat-badge">6%</div>
                <h3>{t('VAT')}</h3>
              </div>
              <p className="section-description">{t('VAT_6_DESCRIPTION')}</p>
              <ul className="vat-list">
                <li>{t('VAT_6_ROYALTIES')}</li>
                <li>{t('VAT_6_TRAINING')}</li>
              </ul>
              <div className="info-badge warning">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M8 1.5C4.41 1.5 1.5 4.41 1.5 8C1.5 11.59 4.41 14.5 8 14.5C11.59 14.5 14.5 11.59 14.5 8C14.5 4.41 11.59 1.5 8 1.5ZM8.75 11.25H7.25V7.25H8.75V11.25ZM8.75 5.75H7.25V4.25H8.75V5.75Z" fill="currentColor"/>
                </svg>
                <span>{t('VAT_6_NOTE')}</span>
              </div>
            </div>
          )}

          {/* 12% VAT */}
          {vatRateToShow === '12' && (
            <div className="vat-single-card vat-12">
              <div className="vat-card-header">
                <div className="vat-badge">12%</div>
                <h3>{t('VAT')}</h3>
              </div>
              <p className="section-description">{t('VAT_12_DESCRIPTION')}</p>
              <ul className="vat-list">
                <li>{t('VAT_12_ARTWORK')}</li>
                <li>{t('VAT_12_FOOD')}</li>
              </ul>
            </div>
          )}

          {/* 0% VAT */}
          {vatRateToShow === '0' && (
            <div className="vat-single-card vat-0">
              <div className="vat-card-header">
                <div className="vat-badge">0%</div>
                <h3>{t('VAT')}</h3>
              </div>
              <p className="section-description">{t('VAT_0_DESCRIPTION')}</p>
              <div className="alert-box">
                <div className="alert-header">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM10 11C9.45 11 9 10.55 9 10V6C9 5.45 9.45 5 10 5C10.55 5 11 5.45 11 6V10C11 10.55 10.55 11 10 11ZM11 15H9V13H11V15Z" fill="currentColor"/>
                  </svg>
                  <strong>{t('VAT_0_WARNING_TITLE')}</strong>
                </div>
                <ul className="alert-list">
                  <li>{t('VAT_0_WARNING_ARTIST')}</li>
                  <li>{t('VAT_0_WARNING_CONTACT')}</li>
                </ul>
              </div>
            </div>
          )}

          {/* Important Banner - Always Show */}
          <div className="important-banner">
            <div className="banner-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 13C11.45 13 11 12.55 11 12V8C11 7.45 11.45 7 12 7C12.55 7 13 7.45 13 8V12C13 12.55 12.55 13 12 13ZM13 17H11V15H13V17Z" fill="currentColor"/>
              </svg>
            </div>
            <div className="banner-content">
              <h4>{t('VAT_IMPORTANT_TITLE')}</h4>
              <p>{t('VAT_IMPORTANT_TEXT')}</p>
              <p className="banner-footer">{t('VAT_IMPORTANT_CONTACT')}</p>
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default VatInfoModal;
